// Build a high-fidelity, email-client-safe HTML newsletter for a single article.
// Uses table-based layout, inline styles, and absolute image URLs so the same
// markup renders correctly in Gmail/Outlook/Apple Mail and as a downloadable .html file.

export type NewsletterArticle = {
  title: string;
  slug: string;
  image_url: string | null;
  header_image_url?: string | null;
  excerpt: string | null;
  body_html: string | null;
  external_url: string | null;
  category: string | null;
  published_at: string;
};

const BRAND = {
  primary: "#1f3a8a", // deep navy/indigo close to original AIA blue
  accent: "#7c3aed", // purple accent
  text: "#1f2937",
  muted: "#4b5563",
  border: "#e5e7eb",
  bg: "#f4f4f7",
  card: "#ffffff",
};

function fmtDate(s: string) {
  return new Date(s).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: "UTC",
  });
}

function absolutize(url: string | null | undefined, origin: string): string {
  if (!url) return "";
  if (/^https?:\/\//i.test(url)) return url;
  if (url.startsWith("/")) return origin.replace(/\/$/, "") + url;
  return url;
}

export function buildNewsletterHtml(
  article: NewsletterArticle,
  origin: string,
): string {
  const hero = absolutize(article.header_image_url || article.image_url, origin);
  const articleUrl = `${origin.replace(/\/$/, "")}/newsletter/${article.slug}`;
  const externalUrl = article.external_url || articleUrl;
  const previewText = (article.excerpt || article.title).slice(0, 140);

  return `<!DOCTYPE html PUBLIC "-//W3C//DTD XHTML 1.0 Transitional//EN" "http://www.w3.org/TR/xhtml1/DTD/xhtml1-transitional.dtd">
<html xmlns="http://www.w3.org/1999/xhtml" lang="en">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width,initial-scale=1" />
  <meta http-equiv="X-UA-Compatible" content="IE=edge" />
  <meta name="x-apple-disable-message-reformatting" />
  <title>${escapeHtml(article.title)} — Aesthetic Insights</title>
  <style>
    @media only screen and (max-width: 620px) {
      .container { width: 100% !important; }
      .px { padding-left: 20px !important; padding-right: 20px !important; }
      .h1 { font-size: 26px !important; line-height: 1.25 !important; }
    }
    a { color: ${BRAND.primary}; }
    .body-copy p { margin: 0 0 16px 0; }
    .body-copy h2 { font-family: Georgia, 'Times New Roman', serif; font-size: 20px; color: ${BRAND.primary}; margin: 28px 0 10px 0; }
  </style>
</head>
<body style="margin:0;padding:0;background:${BRAND.bg};font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;color:${BRAND.text};">
  <div style="display:none;max-height:0;overflow:hidden;opacity:0;font-size:1px;line-height:1px;color:${BRAND.bg};">${escapeHtml(previewText)}</div>
  <table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0" style="background:${BRAND.bg};">
    <tr><td align="center" style="padding:24px 12px;">

      <!-- Masthead -->
      <table role="presentation" class="container" width="600" cellspacing="0" cellpadding="0" border="0" style="width:600px;max-width:600px;background:${BRAND.card};border-radius:8px;overflow:hidden;box-shadow:0 1px 3px rgba(15,23,42,0.08);">
        <tr>
          <td style="background:linear-gradient(90deg, ${BRAND.primary} 0%, ${BRAND.accent} 100%);padding:18px 28px;" class="px">
            <table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0">
              <tr>
                <td style="color:#ffffff;font-family:Georgia,'Times New Roman',serif;font-size:18px;font-weight:700;letter-spacing:0.3px;">
                  Aesthetic Insights
                </td>
                <td align="right" style="color:rgba(255,255,255,0.85);font-size:11px;letter-spacing:0.18em;text-transform:uppercase;">
                  AIA Newsletter
                </td>
              </tr>
            </table>
          </td>
        </tr>

        ${
          hero
            ? `<tr><td style="padding:0;">
              <img src="${hero}" alt="${escapeHtml(article.title)}" width="600" style="display:block;width:100%;max-width:600px;height:auto;border:0;outline:none;" />
            </td></tr>`
            : ""
        }

        <tr>
          <td class="px" style="padding:28px 36px 8px 36px;">
            <div style="font-size:11px;letter-spacing:0.18em;text-transform:uppercase;color:${BRAND.accent};font-weight:700;">
              ${escapeHtml(article.category || "Insights")} &nbsp;•&nbsp; ${escapeHtml(fmtDate(article.published_at))}
            </div>
            <h1 class="h1" style="font-family:Georgia,'Times New Roman',serif;font-size:30px;line-height:1.2;color:${BRAND.text};margin:10px 0 14px 0;font-weight:700;">
              ${escapeHtml(article.title)}
            </h1>
            ${
              article.excerpt
                ? `<p style="font-size:16px;line-height:1.55;color:${BRAND.muted};margin:0 0 8px 0;">${escapeHtml(article.excerpt)}</p>`
                : ""
            }
            <div style="height:1px;background:${BRAND.border};margin:22px 0;"></div>
          </td>
        </tr>

        <tr>
          <td class="px body-copy" style="padding:0 36px 8px 36px;font-size:16px;line-height:1.7;color:${BRAND.text};">
            ${article.body_html || ""}
          </td>
        </tr>

        <tr>
          <td class="px" style="padding:18px 36px 32px 36px;">
            <table role="presentation" cellspacing="0" cellpadding="0" border="0">
              <tr>
                <td style="background:${BRAND.primary};border-radius:6px;">
                  <a href="${externalUrl}" style="display:inline-block;padding:12px 22px;font-size:14px;font-weight:600;color:#ffffff;text-decoration:none;letter-spacing:0.02em;">
                    Read the full story →
                  </a>
                </td>
              </tr>
            </table>
          </td>
        </tr>

        <tr>
          <td style="background:#0f172a;padding:22px 36px;color:#cbd5e1;font-size:12px;line-height:1.6;" class="px">
            <div style="color:#ffffff;font-family:Georgia,'Times New Roman',serif;font-size:15px;font-weight:700;margin-bottom:6px;">
              Aesthetic Industry Association
            </div>
            <div>Industry news, market intelligence, and meeting highlights for executives, clinicians, and investors in medical aesthetics.</div>
            <div style="margin-top:14px;color:#94a3b8;">
              You are receiving this preview because someone on the AIA team requested a sample of Aesthetic Insights.
            </div>
          </td>
        </tr>
      </table>

    </td></tr>
  </table>
</body>
</html>`;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}
